import { useCallback, useEffect, useMemo, useState } from "react";

import { H1, H2, colorsLight, useParsedQueryString, Body2, TertiaryButton } from "@coherehealth/common";
import { useGetQueuesForUser } from "@coherehealth/qm-api";
import { Divider } from "@mui/material";
import { VisualizationDateFilter, DateFilterOptions, CASE_DUE_DATE_DATE_FILTER_OPTIONS } from "../util/ChartUtil";
import { useAuthorized } from "Authorization";
import { useNavigate } from "react-router-dom";
import routes from "routes";
import DueDateChartsContent from "./DueDatesChartsContent";
import StatsBarContent from "./StatsBarContent";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const ReportsDashboard = () => {
  const navigate = useNavigate();
  const canAccessPage = useAuthorized("SUPERVISOR");
  const canAccessProductivity = useAuthorized("PRODUCTIVITY_REPORTS");

  const qs = useParsedQueryString();
  const initializedDate = useMemo(() => (qs.days ? (qs.days as DateFilterOptions) : "14"), [qs]);

  const { data: queues, refetch: refetchQueues } = useGetQueuesForUser({ lazy: true });

  const [dateValue, setDateValue] = useState<DateFilterOptions>(initializedDate);

  useEffect(() => {
    setDateValue(initializedDate);
  }, [initializedDate]);

  useEffect(() => {
    refetchQueues();
  }, [refetchQueues]);

  const changeDateValue = useCallback(
    //updates qs and navigates to route on new date selection
    (newDateValue: DateFilterOptions) => {
      navigate(`${routes.REPORTS.DASHBOARD}?days=${newDateValue}`, { replace: false });
    },
    [navigate]
  );

  useEffect(() => {
    //sets query string as 14 days as default on initial page load
    if (!qs.days || (qs.days !== "7" && qs.days !== "14" && qs.days !== "28")) {
      changeDateValue("14");
    }
  }, [changeDateValue, qs]);

  if (!canAccessPage) {
    return null;
  }

  return (
    <div style={{ backgroundColor: colorsLight.background.mid }}>
      <div
        style={{
          display: "inline-flex",
          justifyContent: "space-between",
          width: "100%",
          paddingTop: 51,
          paddingLeft: 32,
          paddingBottom: 16,
        }}
      >
        <H1>Reports</H1>
        {canAccessProductivity && (
          <TertiaryButton onClick={() => window.open(`/productivity`)} style={{ height: 32 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <OpenInNewIcon style={{ marginRight: 11 }} />
              <Body2>View productivity report</Body2>
            </div>
          </TertiaryButton>
        )}
      </div>
      <Divider />
      <div style={{ padding: "0px 32px 16px" }}>
        <StatsBarContent queues={queues} />
        <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: -8 }}>
          <H2 style={{ width: 350 }}>Volume by due date</H2>
          <VisualizationDateFilter
            dateValue={dateValue}
            setDateValue={changeDateValue}
            options={CASE_DUE_DATE_DATE_FILTER_OPTIONS}
          />
        </div>
        <DueDateChartsContent dateValue={dateValue} queues={queues} />
      </div>
    </div>
  );
};

export default ReportsDashboard;
